import { Box, Input, useColorModeValue } from "@chakra-ui/react";
import { CustomCellEditorProps } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

export type TextCellEditorProps = CustomCellEditorProps & {
  onNoteUpdate?: (encounterId: string, note: string, immediate?: boolean) => void;
};

export const TextCellEditor = ({
  value,
  onValueChange,
  eventKey,
  stopEditing,
  data,
  onNoteUpdate,
  column,
}: TextCellEditorProps) => {
  const refInput = useRef<HTMLInputElement>(null);
  const [currentValue, setCurrentValue] = useState<string>(value || "");
  const bgColor = useColorModeValue("white", "gray.800");

  const updateValue = (val: string) => {
    const newValue = val === "" ? null : val;
    setCurrentValue(val);
    onValueChange(newValue);

    if (onNoteUpdate && data?.id) {
      onNoteUpdate(data.id, val || "");
    }
  };

  useEffect(() => {
    let startValue;

    if (eventKey === "Backspace") {
      startValue = "";
    } else if (eventKey && eventKey.length === 1) {
      startValue = eventKey;
    } else {
      startValue = value;
    }
    if (startValue == null) {
      startValue = "";
    }

    setCurrentValue(startValue);
    // Only update if there's an actual change from the original value
    if (startValue !== value) {
      updateValue(startValue);
    }
    refInput.current?.focus();
  }, []);

  // Handle Enter key to stop editing
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      stopEditing();
    }
  };

  // Get column width for popup editor
  const getColumnWidth = () => column?.getActualWidth() ?? 250;

  return (
    <Box bg={bgColor} width={`${getColumnWidth()}px`}>
      <Input
        value={currentValue}
        spellCheck={false}
        ref={refInput}
        onChange={event => updateValue(event.target.value)}
        onBlur={() => stopEditing()}
        onKeyDown={handleKeyDown}
      />
    </Box>
  );
};

export default TextCellEditor;
